/**
 * practice-hours — "Open today 08:00 – 18:00" for the practice menu's rows, from a practice's working-hour summary
 * (Practice app `todayHours`). The wire times are 12-hour with a marker ("06:00 AM", "03:00 PM"); the line is 24-hour
 * (the schedule's one clock look). A practice with hours but none today reads "Closed today"; with none at all, null.
 */
import type { WorkingHourSummary } from '@/types/settings-api';
import { practiceNow } from '@/shared/lib/practice-time';

const PRACTICE_WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** "03:00 PM" → "15:00"; anything unreadable → null. */
export function to24Hour(raw: string | null | undefined): string | null {
  const m = /^\s*(\d{1,2}):(\d{2})\s*(AM|PM)?\s*$/i.exec(raw ?? '');
  if (!m) return null;
  let h = Number(m[1]);
  const marker = m[3]?.toUpperCase();
  if (marker === 'PM' && h < 12) h += 12;
  if (marker === 'AM' && h === 12) h = 0;
  if (h > 23) return null;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
}

export function todayHoursLine(hours: WorkingHourSummary[] | null | undefined, now: Date = practiceNow()): string | null {
  const today = now.getDay();
  const idx = (d?: string | null) => (d ? PRACTICE_WEEKDAYS.indexOf(d.trim().toLowerCase()) : -1);
  const covers = (h: WorkingHourSummary) => {
    const one = idx(h.weekday);
    if (one >= 0) return one === today;
    const a = idx(h.startWeekDay);
    const b = idx(h.endWeekDay);
    if (a < 0) return false;
    if (b < 0) return a === today;
    return a <= b ? today >= a && today <= b : today >= a || today <= b;
  };
  const list = hours ?? [];
  const slot = list.find((h) => covers(h) && to24Hour(h.startTime) && to24Hour(h.endTime));
  if (!slot) return list.length ? 'Closed today' : null;
  return `Open today ${to24Hour(slot.startTime)} – ${to24Hour(slot.endTime)}`;
}
